"use client";

import Image from "next/image";
import { useEffect, useState, useRef } from "react";
import { useSession } from "next-auth/react";
import { formatDistanceToNow } from "date-fns";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { sendMessage, markConversationAsRead, deleteMessage, checkIsFavorite, sendTypingIndicator, editMessage } from "@/app/actions/messaging";
import { getPusherClient } from "@/lib/pusher-client";
import { Send, Trash2, Bug, ExternalLink, Clock, CheckCheck, Paperclip, X, FileText, Film, Music, File, ImageIcon, Pencil, Check } from "lucide-react";
import { prepareMessageAttachment } from "@/app/actions/message-attachment";
import { toast } from "sonner";
import { UserProfilePopover } from "./user-profile-popover";
import { OnlineIndicator } from "@/components/ui/online-indicator";

interface MessageAttachment {
  id: string;
  url: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  type: "IMAGE" | "VIDEO" | "DOCUMENT" | "AUDIO" | "OTHER";
  thumbnailUrl?: string;
  width?: number;
  height?: number;
}

interface Message {
  id: string;
  content: string;
  createdAt: string;
  isRead: boolean;
  sender: {
    id: string;
    name: string | null;
    email: string | null;
    image: string | null;
  };
  attachments?: MessageAttachment[];
}

interface ConversationThreadProps {
  conversationId: string;
  otherUser: {
    id: string;
    name: string | null;
    email: string | null;
    image: string | null;
  };
  listing?: {
    id: string;
    title: string;
    address: string;
    price: number;
    images: string[];
  } | null;
  bugReport?: {
    id: string;
    category: string;
    priority: string;
    status: string;
    url: string;
    description: string;
  } | null;
}

export function ConversationThread({
  conversationId,
  otherUser,
  listing,
  bugReport,
}: ConversationThreadProps) {
  const { data: session } = useSession();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  const [isOtherUserFavorite, setIsOtherUserFavorite] = useState(false);
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [pendingAttachments, setPendingAttachments] = useState<MessageAttachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);

  // Check if the other user is a favorite contact
  useEffect(() => {
    async function checkFavorite() {
      if (otherUser?.id) {
        const result = await checkIsFavorite(otherUser.id);
        setIsOtherUserFavorite(result.isFavorite);
      }
    }
    checkFavorite();
  }, [otherUser?.id]);

  useEffect(() => {
    fetchMessages();
    markAsRead();

    // Subscribe to real-time updates
    const pusher = getPusherClient();
    const channel = pusher.subscribe(`conversation-${conversationId}`);

    channel.bind("new-message", (data: { message: Message }) => {
      // Check if message already exists (avoid duplicates from optimistic updates)
      setMessages((prev) => {
        const exists = prev.some((m) => m.id === data.message.id);
        if (exists) {
          return prev; // Message already exists, don't add it again
        }
        return [...prev, data.message];
      });
      markAsRead();
    });

    // Listen for typing indicators
    channel.bind("typing", (data: { userId: string; isTyping: boolean }) => {
      if (data.userId !== session?.user?.id) {
        setIsOtherUserTyping(data.isTyping);
        // Auto-clear typing indicator after 3 seconds (safety)
        if (data.isTyping) {
          setTimeout(() => setIsOtherUserTyping(false), 3000);
        }
      }
    });

    return () => {
      channel.unbind_all();
      channel.unsubscribe();
    };
  }, [conversationId]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  async function fetchMessages() {
    try {
      const response = await fetch(
        `/api/conversations/${conversationId}/messages`,
      );
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || data);
        setHasMore(data.hasMore || false);
        setCursor(data.nextCursor || null);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  }

  async function loadMoreMessages() {
    if (!cursor || loadingMore) return;

    setLoadingMore(true);
    try {
      const response = await fetch(
        `/api/conversations/${conversationId}/messages?cursor=${cursor}`,
      );
      if (response.ok) {
        const data = await response.json();
        // Prepend older messages
        setMessages((prev) => [...(data.messages || []), ...prev]);
        setHasMore(data.hasMore || false);
        setCursor(data.nextCursor || null);
      }
    } catch (error) {
      console.error("Failed to load more messages:", error);
      toast.error("Failed to load older messages");
    } finally {
      setLoadingMore(false);
    }
  }

  async function markAsRead() {
    await markConversationAsRead(conversationId);
  }

  // Handle typing indicator with debounce
  function handleTyping() {
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Send typing start
    sendTypingIndicator(conversationId, true).catch(console.error);

    // Set timeout to send typing stop after 2 seconds of no typing
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(conversationId, false).catch(console.error);
    }, 2000);
  }

  async function handleSend() {
    if ((!inputValue.trim() && pendingAttachments.length === 0) || sending) return;

    const content = inputValue.trim();
    setInputValue("");
    setSending(true);

    // Store attachments to send and clear pending
    const attachmentsToSend = [...pendingAttachments];
    setPendingAttachments([]);

    // Optimistic update: add message immediately with temp ID
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage: Message = {
      id: tempId,
      content,
      createdAt: new Date().toISOString(),
      isRead: false,
      sender: {
        id: session?.user?.id || "",
        name: session?.user?.name || null,
        email: session?.user?.email || null,
        image: session?.user?.image || null,
      },
      attachments: attachmentsToSend,
    };

    setMessages((prev) => [...prev, optimisticMessage]);

    try {
      const result = await sendMessage(
        conversationId,
        content,
        attachmentsToSend.map(a => ({
          url: a.url,
          fileName: a.fileName,
          fileSize: a.fileSize,
          mimeType: a.mimeType,
          thumbnailUrl: a.thumbnailUrl,
          width: a.width,
          height: a.height,
        }))
      );

      if (result.error) {
        // Remove optimistic message on error
        setMessages((prev) => prev.filter((m) => m.id !== tempId));
        toast.error(result.error);
        setInputValue(content); // Restore input on error
        setPendingAttachments(attachmentsToSend); // Restore attachments on error
      } else if (result.message) {
        // Replace optimistic message with real one from server
        // This prevents duplicates when Pusher also delivers the message
        setMessages((prev) => {
          const filtered = prev.filter((m) => m.id !== tempId);
          // Check if real message already exists (from Pusher racing)
          const exists = filtered.some((m) => m.id === result.message!.id);
          if (exists) {
            return filtered;
          }
          return [...filtered, result.message!];
        });
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      toast.error("Failed to send message");
      setInputValue(content); // Restore input on error
      setPendingAttachments(attachmentsToSend); // Restore attachments on error
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingAttachment(true);

    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const result = await prepareMessageAttachment(file.name, file.type, file.size);

        if (result.error || !result.presignedUrl || !result.attachment) {
          throw new Error(result.error || "Failed to prepare upload");
        }

        // Upload to S3 using presigned URL
        const uploadResponse = await fetch(result.presignedUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error("Failed to upload file");
        }

        return result.attachment;
      });

      const uploadedAttachments = await Promise.all(uploadPromises);
      setPendingAttachments((prev) => [...prev, ...uploadedAttachments]);
    } catch (error) {
      console.error("File upload error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to upload file");
    } finally {
      setUploadingAttachment(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }

  function removePendingAttachment(index: number) {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  }

  function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  function getAttachmentIcon(type: string) {
    switch (type) {
      case "IMAGE": return null; // Will show thumbnail
      case "VIDEO": return <Film className="h-8 w-8" />;
      case "AUDIO": return <Music className="h-8 w-8" />;
      case "DOCUMENT": return <FileText className="h-8 w-8" />;
      default: return <File className="h-8 w-8" />;
    }
  }

  function startEditMessage(messageId: string, currentContent: string) {
    setEditingMessageId(messageId);
    setEditContent(currentContent);
  }

  function cancelEdit() {
    setEditingMessageId(null);
    setEditContent("");
  }

  async function handleEditSubmit(messageId: string) {
    if (!editContent.trim()) return;

    try {
      const result = await editMessage(messageId, editContent.trim());

      if (result.error) {
        toast.error(result.error);
      } else {
        // Update message in local state
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId ? { ...m, content: editContent.trim() } : m
          )
        );
        toast.success("Message edited");
        cancelEdit();
      }
    } catch (error) {
      console.error("Failed to edit message:", error);
      toast.error("Failed to edit message");
    }
  }

  function canEditMessage(message: Message): boolean {
    // Can only edit own messages within 15 minutes
    if (message.sender.id !== session?.user?.id) return false;
    const messageAge = Date.now() - new Date(message.createdAt).getTime();
    const editWindowMs = 15 * 60 * 1000; // 15 minutes
    return messageAge <= editWindowMs;
  }

  async function handleDeleteClick(messageId: string) {
    const confirmed = window.confirm("Are you sure you want to delete this message? This action cannot be undone.");
    if (!confirmed) return;

    setDeletingMessageId(messageId);

    try {
      const result = await deleteMessage(messageId);

      if (result.error) {
        toast.error(result.error);
      } else {
        // Remove message from local state
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
        toast.success("Message deleted");
      }
    } catch (error) {
      console.error("Failed to delete message:", error);
      toast.error("Failed to delete message");
    } finally {
      setDeletingMessageId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-16rem)] flex-col">
        <div className="flex-1 space-y-4 p-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-16 w-64" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-16rem)] flex-col">
      {/* Bug Report context */}
      {bugReport && (
        <div className="border-b border-orange-500/20 p-4 bg-orange-500/5">
          <div className="flex items-start gap-3">
            <Bug className="h-5 w-5 text-orange-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-sm">Bug Report</span>
                <Badge className="text-xs" variant="outline">{bugReport.status}</Badge>
                <Badge variant="outline" className="text-xs">
                  {bugReport.priority}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                {bugReport.description}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  Category: {bugReport.category.replace(/_/g, " ")}
                </span>
                <a
                  href={bugReport.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-orange-500 hover:text-orange-600 flex items-center gap-1"
                >
                  View page <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Listing context */}
      {listing && (
        <div className="border-b p-4">
          <div className="flex items-center gap-3">
            {listing.images[0] && (
              <Image
                src={listing.images[0]}
                alt={listing.title}
                width={48}
                height={48}
                className="h-12 w-12 rounded object-cover"
              />
            )}
            <div>
              <h3 className="font-semibold text-sm">{listing.title}</h3>
              <p className="text-xs text-muted-foreground">{listing.address}</p>
              <p className="text-xs font-semibold text-primary">
                ${listing.price.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
        <div className="space-y-4">
          {/* Load more button */}
          {hasMore && (
            <div className="text-center py-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={loadMoreMessages}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading..." : "Load older messages"}
              </Button>
            </div>
          )}
          {messages.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message, index) => {
              const isOwnMessage = message.sender.id === session?.user?.id;
              const isLastMessage = index === messages.length - 1;
              const isOptimistic = message.id.startsWith("temp-");

              return (
                <div
                  key={message.id}
                  ref={isLastMessage ? lastMessageRef : null}
                  className={`group flex gap-3 ${isOwnMessage ? "flex-row-reverse" : ""}`}
                >
                  {isOwnMessage ? (
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={message.sender.image || undefined} />
                      <AvatarFallback>
                        {message.sender.name?.[0] || "U"}
                      </AvatarFallback>
                    </Avatar>
                  ) : (
                    <UserProfilePopover
                      user={message.sender}
                      isFavorite={isOtherUserFavorite}
                      onFavoriteChange={() => setIsOtherUserFavorite(!isOtherUserFavorite)}
                      showMessageButton={false}
                      side="left"
                      align="start"
                    >
                      <div className="relative">
                        <Avatar className="h-8 w-8 transition-all hover:ring-2 hover:ring-cyan-500/50">
                          <AvatarImage src={message.sender.image || undefined} />
                          <AvatarFallback>
                            {message.sender.name?.[0] || "U"}
                          </AvatarFallback>
                        </Avatar>
                        <OnlineIndicator userId={message.sender.id} position="bottom-right" size="sm" />
                      </div>
                    </UserProfilePopover>
                  )}

                  <div
                    className={`flex flex-col gap-1 max-w-[70%] ${isOwnMessage ? "items-end" : ""}`}
                  >
                    <div className="relative">
                      <div
                        className={`rounded-lg px-4 py-2 ${
                          isOwnMessage
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted"
                        }`}
                      >
                        {/* Attachments */}
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="mb-2 space-y-2">
                            {message.attachments.map((att) => (
                              <div key={att.id}>
                                {att.type === "IMAGE" ? (
                                  <img
                                    src={att.url}
                                    alt={att.fileName}
                                    className="max-w-full rounded cursor-pointer hover:opacity-90 transition-opacity"
                                    style={{ maxHeight: "200px" }}
                                    onClick={() => window.open(att.url, "_blank")}
                                  />
                                ) : att.type === "VIDEO" ? (
                                  <video
                                    src={att.url}
                                    controls
                                    className="max-w-full rounded"
                                    style={{ maxHeight: "200px" }}
                                  />
                                ) : (
                                  <a
                                    href={att.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-2 p-2 rounded hover:bg-muted/50 ${
                                      isOwnMessage ? "text-primary-foreground" : ""
                                    }`}
                                  >
                                    {getAttachmentIcon(att.type)}
                                    <div className="text-xs">
                                      <p className="font-medium truncate max-w-[150px]">{att.fileName}</p>
                                      <p className="opacity-70">{formatFileSize(att.fileSize)}</p>
                                    </div>
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Message text */}
                        {editingMessageId === message.id ? (
                          <div className="flex flex-col gap-2">
                            <Textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              className="min-h-[60px] resize-none bg-background text-foreground"
                              autoFocus
                            />
                            <div className="flex gap-2 justify-end">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={cancelEdit}
                                className="h-7 px-2"
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleEditSubmit(message.id)}
                                disabled={!editContent.trim()}
                                className="h-7 px-2"
                              >
                                <Check className="h-3 w-3 mr-1" />
                                Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          message.content && (
                            <p className="text-sm whitespace-pre-wrap break-words">
                              {message.content}
                            </p>
                          )
                        )}
                      </div>

                      {/* Edit/Delete buttons - only show on hover and for own messages */}
                      {isOwnMessage && editingMessageId !== message.id && (
                        <div className={`absolute ${isOwnMessage ? "-left-16" : "-right-16"} top-1/2 -translate-y-1/2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-[var(--z-notification)]`}>
                          {canEditMessage(message) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"
                              onClick={() => startEditMessage(message.id, message.content)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 bg-destructive/10 hover:bg-destructive/20 text-destructive"
                            onClick={() => handleDeleteClick(message.id)}
                            disabled={deletingMessageId === message.id}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(message.createdAt), {
                        addSuffix: true,
                      })}
                      {/* Message status indicator */}
                      {isOwnMessage && (
                        <span className="ml-2">
                          {isOptimistic ? (
                            <Clock className="h-3 w-3 inline opacity-50" />
                          ) : message.isRead ? (
                            <CheckCheck className="h-3 w-3 inline text-blue-500" />
                          ) : (
                            <CheckCheck className="h-3 w-3 inline opacity-50" />
                          )}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Typing indicator */}
      {isOtherUserTyping && (
        <div className="px-4 py-2 text-sm text-muted-foreground flex items-center gap-2">
          <span className="flex gap-1">
            <span className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
          </span>
          <span>{otherUser?.name || "User"} is typing...</span>
        </div>
      )}

      {/* Input */}
      <div className="border-t p-4">
        {/* Pending attachments */}
        {pendingAttachments.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {pendingAttachments.map((att, idx) => (
              <div
                key={idx}
                className="relative group flex items-center gap-2 bg-muted rounded-lg p-2 pr-8"
              >
                {att.type === "IMAGE" ? (
                  <img
                    src={att.url}
                    alt={att.fileName}
                    className="h-12 w-12 object-cover rounded"
                  />
                ) : (
                  <div className="h-12 w-12 flex items-center justify-center bg-muted-foreground/10 rounded">
                    {getAttachmentIcon(att.type)}
                  </div>
                )}
                <div className="text-xs">
                  <p className="font-medium truncate max-w-[100px]">{att.fileName}</p>
                  <p className="text-muted-foreground">{formatFileSize(att.fileSize)}</p>
                </div>
                <button
                  onClick={() => removePendingAttachment(idx)}
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Attachment button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingAttachment || sending}
            className="shrink-0"
          >
            <Paperclip className="h-5 w-5" />
          </Button>

          <Textarea
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              handleTyping();
            }}
            onKeyDown={handleKeyDown}
            placeholder={pendingAttachments.length > 0 ? "Add a message (optional)..." : "Type a message..."}
            className="min-h-[80px] resize-none flex-1"
            disabled={sending}
          />
          <Button
            onClick={handleSend}
            disabled={(!inputValue.trim() && pendingAttachments.length === 0) || sending || uploadingAttachment}
            size="icon"
            className="h-[80px]"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
